import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';

const Step = styled.li`
  display: flex;
  list-style: none;
`;

const IconColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const IconCircle = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  opacity: 0.4;
`;

const Line = styled.div`
  flex: 1;
  width: 1px;
  opacity: 0.25;
`;

const ContentColumn = styled.div`
  flex: 1;
  margin-left: 1rem;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
`;

const Body = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

/**
 * Skeleton for a single timeline step, mirroring the TradeRuleTimelineStep layout:
 * icon column (circle + optional connecting line) and a content column (header + card body).
 * @param {object} props
 * @param {boolean} props.hasLine - Whether to render the vertical connecting line below the icon.
 * @param {boolean} props.hasActions - Whether to render action-button placeholders in the body.
 * @returns {React.ReactElement} - TimelineStepSkeleton component
 */
function TimelineStepSkeleton({ hasLine, hasActions }) {
  return (
    <Step>
      <IconColumn>
        <IconCircle className="placeholder-glow bg-darken" />
        {hasLine && <Line className="border border-dark" />}
      </IconColumn>
      <ContentColumn>
        <Header>
          <span className="placeholder placeholder-glow bg-darken" style={{ width: '13.75rem', height: '1rem' }} />
          <span className="placeholder placeholder-glow bg-darken" style={{ width: '7.5rem', height: '1rem' }} />
        </Header>
        <Body className="card card-body mb-3 mt-3">
          <ul className="mt-0 mx-0 mb-n3 p-0" style={{ listStyle: 'none' }}>
            <li className="mb-3 d-flex align-items-center">
              <span className="placeholder placeholder-glow bg-darken rounded-circle me-2" style={{ width: '2.5rem', height: '2.5rem', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <span className="placeholder placeholder-glow bg-darken d-block mb-1" style={{ width: '60%', height: '1rem' }} />
                <span className="placeholder placeholder-glow bg-darken d-block" style={{ width: '40%', height: '0.875rem' }} />
              </div>
            </li>
            <li className="mb-3">
              <span className="placeholder placeholder-glow bg-darken d-block mb-1" style={{ width: '80%', height: '0.875rem' }} />
              <span className="placeholder placeholder-glow bg-darken d-block" style={{ width: '65%', height: '0.875rem' }} />
            </li>
            {hasActions && (
              <li className="mb-3">
                <span className="placeholder placeholder-glow bg-darken" style={{ width: '5.625rem', height: '2rem', borderRadius: '0.25rem' }} />
              </li>
            )}
          </ul>
        </Body>
      </ContentColumn>
    </Step>
  );
}

TimelineStepSkeleton.propTypes = {
  hasLine: PropTypes.bool.isRequired,
  hasActions: PropTypes.bool.isRequired,
};

/**
 * Placeholder component that displays a loading skeleton for the TradeRuleCustomerShow page.
 * Renders real breadcrumb and heading text, with a timeline skeleton that mirrors the
 * structure of TradeRuleTimelineStep (icon column, connecting line, header, and card body).
 * @returns {React.ReactElement} - Placeholder component
 */
function Placeholder() {
  const intl = useIntl();

  const tradeRulesLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_customer_show.placeholder.breadcrumbs.trade_rules.label',
    defaultMessage: 'Trade Rules',
  });
  const tradeRuleHeading = intl.formatMessage({
    id: 'trade_rule.trade_rule_customer_show.placeholder.heading.trade_rule',
    defaultMessage: 'Trade Rule',
  });

  const tradeRuleContext = intl.formatMessage({
    id: 'trade_rule.trade_rule_customer_show.placeholder.heading.context',
    defaultMessage: 'Rule',
  });

  return (
    <div aria-hidden="true" inert>
      <Breadcrumbs
        breadcrumbs={[
          { name: tradeRulesLabel, path: '/trade-rules' },
          { name: <span className="placeholder placeholder-glow bg-darken" style={{ width: '7.5rem' }} /> },
        ]}
      />
      <Heading title={tradeRuleHeading} context={tradeRuleContext} />

      <ol className="m-0 p-0">
        <TimelineStepSkeleton hasLine hasActions={false} />
        <TimelineStepSkeleton hasLine={false} hasActions />
      </ol>
    </div>
  );
}

export default Placeholder;
